import { inverseChainData, inverseConfigFor } from '@1delta/data-sdk'
import {
  CurveTricryptoOracleAbi,
  Erc20Abi,
  InverseDbrAbi,
  InverseMarketAbi,
  InverseOracleAbi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { InverseMarketRaw, InverseMarketsRaw } from './types'

/**
 * Hybrid public-data fetch for Inverse FiRM — the protocol's own API
 * first (one call returns every market with CF/liquidation params,
 * `dolaLiquidity`, `leftToBorrow`, price and pause state), a flat
 * on-chain multicall as fallback. The market ROSTER always comes from
 * lender-metadata (`inverseConfig`/`inverseMarkets`) — the API is a
 * value source, never a discovery source, so a compromised API cannot
 * inject markets.
 *
 * The borrow APR is the DBR price in DOLA (interest is prepaid in DBR
 * at 1 DBR per DOLA-year). FiRM itself prices no DBR — its oracle has no
 * DBR feed and `liquidate` reads only the COLLATERAL price — so the rate
 * is a MARKET price, and the market is one Curve tricrypto pool (TriDBR:
 * DOLA/DBR/INV). We read that pool's own `price_oracle(0)` EMA FIRST,
 * because it is the venue itself rather than someone's reading of it;
 * `/api/dbr` `priceDola` (which serves this same pool's price — the two
 * agree to ~1 bp) is the fallback, and the metadata snapshot the last
 * resort. A depth-aware read is still pair-book work; this is the mid.
 *
 * `replenishmentPriceBps` — the force-replenish penalty — is read
 * ON-CHAIN on BOTH paths, in parallel with the API. It is a chain-wide
 * governance dial on the DBR contract, so it costs one call per fetch,
 * and the API has never served it: reading it only in the fallback left
 * it absent from every row the API path produced, i.e. essentially all
 * of them.
 */

const FIXED_MARKETS_URL = 'https://www.inverse.finance/api/f2/fixed-markets'
const DBR_URL = 'https://www.inverse.finance/api/dbr'

const INVERSE_PUBLIC_READ_ABI = [
  ...InverseMarketAbi,
  ...InverseOracleAbi,
  ...InverseDbrAbi,
  ...CurveTricryptoOracleAbi,
  ...Erc20Abi,
] as any

// 5 reads per market in the fallback multicall (keep in sync with slicer).
// The DBR-wide reads are NOT in this batch — see `fetchReplenishmentPriceBps`.
const READS_PER_MARKET = 5

const fetchJson = async (url: string, timeoutMs = 6000): Promise<any> => {
  const res = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(timeoutMs),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

/** The two chain-wide FiRM numbers — neither is per-market. */
type InverseChainWide = {
  /** DBR price in DOLA = the fixed borrow APR as a decimal. */
  dbrPriceDola: number | null
  /** Force-replenish penalty in bps (5475 = 54.75% APR). */
  replenishmentPriceBps: number | null
}

const toNum = (v: any): number | null => {
  if (typeof v === 'bigint') return Number(v)
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * One multicall for everything FiRM publishes chain-wide, both read from
 * the contracts that own them:
 *
 *  - `replenishmentPriceBps` — the DBR contract's own governance dial, and
 *    the ONLY DBR→DOLA conversion anywhere in the protocol (`forceReplenish`
 *    charges `amount × bps/10000` DOLA of new debt). It is NOT the ongoing
 *    borrow rate: it is the penalty for running dry, ~11x the market price.
 *  - the DBR price — from the Curve pool that IS the DBR market.
 *    `price_oracle(0)` prices `coins(1)` in `coins(0)`, so we read both
 *    coins and PROVE the ordering is DOLA-then-DBR before trusting it; a
 *    pool that does not match is refused rather than read at the wrong
 *    index, which would publish an INV-denominated number as an APR.
 *
 * Never throws — an unreadable value is reported ABSENT, never guessed.
 */
const fetchInverseChainWide = async (
  chainId: string,
  config: { dbr: string; dola: string; dbrPricePool?: string },
): Promise<InverseChainWide> => {
  const pool = config.dbrPricePool
  const calls: any[] = [
    { address: config.dbr, name: 'replenishmentPriceBps', params: [] },
  ]
  if (pool) {
    calls.push(
      { address: pool, name: 'coins', params: [0n] },
      { address: pool, name: 'coins', params: [1n] },
      { address: pool, name: 'price_oracle', params: [0n] },
    )
  }
  try {
    const r = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: INVERSE_PUBLIC_READ_ABI,
      allowFailure: true,
    })) as any[]
    const replenishmentPriceBps = toNum(r[0])
    if (!pool) return { dbrPriceDola: null, replenishmentPriceBps }
    const same = (a: any, b: string) =>
      typeof a === 'string' && a.toLowerCase() === b.toLowerCase()
    const ordered = same(r[1], config.dola) && same(r[2], config.dbr)
    const raw = typeof r[3] === 'bigint' ? Number(r[3]) / 1e18 : NaN
    return {
      dbrPriceDola: ordered && Number.isFinite(raw) && raw > 0 ? raw : null,
      replenishmentPriceBps,
    }
  } catch {
    return { dbrPriceDola: null, replenishmentPriceBps: null }
  }
}

/**
 * DBR price (decimal DOLA per DBR = the fixed borrow APR): the pool's own
 * EMA first, Inverse's API second, the metadata snapshot last.
 */
const resolveDbrPriceDola = async (
  onChain: number | null,
  snapshot: string | undefined,
): Promise<number | null> => {
  if (onChain !== null) return onChain
  try {
    const d = await fetchJson(DBR_URL)
    const p = Number(d?.priceDola)
    if (Number.isFinite(p) && p > 0) return p
  } catch {}
  const snap = Number(snapshot)
  return Number.isFinite(snap) && snap > 0 ? snap : null
}

export async function fetchInverseMarkets(
  lender: string,
  chainId: string,
): Promise<InverseMarketsRaw> {
  const config = inverseConfigFor(lender, chainId)
  const chainData = inverseChainData(lender, chainId)
  const markets = chainData?.markets ?? []
  const empty: InverseMarketsRaw = {
    lender,
    config,
    chainData,
    dbrPriceDola: null,
    replenishmentPriceBps: null,
    markets: [],
    source: 'none',
  }
  if (!config || markets.length === 0) return empty

  // One chain-wide multicall, then the price ladder on top of it. Both are
  // kicked off before the API fetch so the on-chain leg costs no wall clock.
  const chainWidePromise = fetchInverseChainWide(chainId, config)
  const dbrPricePromise = chainWidePromise.then((c) =>
    resolveDbrPriceDola(c.dbrPriceDola, config.dbrPriceDolaSnapshot),
  )
  const replenishPromise = chainWidePromise.then((c) => c.replenishmentPriceBps)

  // --- primary: the protocol API ---
  try {
    const api = await fetchJson(FIXED_MARKETS_URL)
    const byAddr: { [addr: string]: any } = {}
    for (const m of api?.markets ?? []) {
      if (m?.address) byAddr[String(m.address).toLowerCase()] = m
    }
    if (Object.keys(byAddr).length > 0) {
      const rows: InverseMarketRaw[] = markets.map((market) => {
        const m = byAddr[market.address.toLowerCase()]
        const num = (v: any): number | null =>
          typeof v === 'number' && Number.isFinite(v) ? v : null
        return {
          market,
          totalDebt: num(m?.totalDebt),
          dolaLiquidity: num(m?.dolaLiquidity),
          leftToBorrow: num(m?.leftToBorrow),
          price: num(m?.price),
          borrowPaused:
            typeof m?.borrowPaused === 'boolean' ? m.borrowPaused : null,
          dailyBorrows: num(m?.dailyBorrows),
          // The API serves it as a DECIMAL (`replenishmentIncentive: 0.1`),
          // unlike the on-chain bps — normalize here, and fall back to the
          // curated metadata value when the field is missing.
          replenishmentIncentiveBps: (() => {
            const dec = num(m?.replenishmentIncentive)
            if (dec !== null) return Math.round(dec * 10000)
            const meta = Number(market.replenishmentIncentiveBps)
            return Number.isFinite(meta) ? meta : null
          })(),
        }
      })
      return {
        lender,
        config,
        chainData,
        dbrPriceDola: await dbrPricePromise,
        // On-chain is authoritative; the API field is a backstop that does
        // not exist today (see `num`).
        replenishmentPriceBps:
          (await replenishPromise) ?? num(api, 'replenishmentPriceBps'),
        markets: rows,
        source: 'api',
      }
    }
  } catch {}

  // --- fallback: flat on-chain multicall ---
  const calls = [
    ...markets.flatMap((m) => [
      { address: m.address, name: 'totalDebt', params: [] },
      { address: m.address, name: 'borrowPaused', params: [] },
      { address: config.dola, name: 'balanceOf', params: [m.address] },
      {
        address: config.oracle,
        name: 'viewPrice',
        params: [m.collToken, BigInt(m.collateralFactorBps)],
      },
      { address: m.address, name: 'replenishmentIncentiveBps', params: [] },
    ]),
  ]
  try {
    const results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: INVERSE_PUBLIC_READ_ABI,
      allowFailure: true,
    })) as any[]
    const bigAt = (i: number): bigint | null => {
      const r = results[i]
      if (typeof r === 'bigint') return r
      if (typeof r === 'number') return BigInt(r)
      return null
    }
    const debtDecimals = config.debtDecimals ?? 18
    const rows: InverseMarketRaw[] = markets.map((market, i) => {
      const base = i * READS_PER_MARKET
      const totalDebt = bigAt(base)
      const paused = results[base + 1]
      const liq = bigAt(base + 2)
      const price = bigAt(base + 3)
      const replenishIncentive = bigAt(base + 4)
      return {
        market,
        totalDebt:
          totalDebt !== null ? Number(totalDebt) / 10 ** debtDecimals : null,
        dolaLiquidity: liq !== null ? Number(liq) / 10 ** debtDecimals : null,
        // dailyBorrows lives on the BorrowController (day-bucketed
        // mapping) — the fallback conservatively caps by liquidity only.
        leftToBorrow: null,
        // viewPrice scales so that price * balance / 1e18 is USD-ish:
        // 10^(36 − collDecimals) per unit — normalize to USD per token.
        price:
          price !== null
            ? Number(price) / 10 ** (36 - market.collDecimals)
            : null,
        borrowPaused: typeof paused === 'boolean' ? paused : null,
        dailyBorrows: null,
        replenishmentIncentiveBps:
          replenishIncentive !== null
            ? Number(replenishIncentive)
            : (() => {
                const meta = Number(market.replenishmentIncentiveBps)
                return Number.isFinite(meta) ? meta : null
              })(),
      }
    })
    return {
      lender,
      config,
      chainData,
      dbrPriceDola: await dbrPricePromise,
      replenishmentPriceBps: await replenishPromise,
      markets: rows,
      source: 'chain',
    }
  } catch {
    return {
      ...empty,
      dbrPriceDola: await dbrPricePromise,
      replenishmentPriceBps: await replenishPromise,
    }
  }
}

// The API top-level payload has no replenishment field today — helper keeps
// the call sites tidy and the read future-proof if they add it.
function num(api: any, key: string): number | null {
  const v = api?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}
